import React, { useState, useEffect } from "react";
import { Input, TextArea } from "components";
import { FlexRow, Button, LineButton, FlexColumn } from "appStyles";
import { createMuiTheme, ThemeProvider } from "@material-ui/core/styles";
import styled, { css } from "react-emotion";
import Switch from "@material-ui/core/Switch";
import { withStyles } from "@material-ui/core/styles";
import {
  withRedux,
  priceToDollars,
  formatDollarsToCents,
  SecureLocalStorage,
  isLoading,
  parseQuery,
} from "helpers";

import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import InvoicePaymentForm from "./InvoicePaymentForm";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Setup Stripe.js and the Elements provider
const stripePromise = loadStripe(__STRIPE_API_KEY__);

const theme = createMuiTheme({
  palette: {
    primary: {
      main: "#F17F7F",
    },
    secondary: {
      main: "#88d1d4",
    },
  },
  overrides: {
    // Style sheet name ⚛️
    MuiTablePagination: {
      // Name of the rule
      actions: {
        // Some CSS
        marginLeft: "0px",
      },
    },
    MuiTableContainer: {
      root: {
        flex: 1,
        height: "100%",
        overflowY: "auto",
        overflowX: "hidden",
        display: "flex",
        width: "100%",
      },
    },
    MuiTableSortLabel: {
      root: {
        color: "#89D2D3 !important",
      },
    },
  },
});

const storeContainer = css`
  margin: 10px 20px;
  @media only screen and (min-width: 768px) {
    margin: 10px 40px;
  }
`;
const headerText = css`
  text-align: center;
  font-size: 2rem;
  margin: 20px;
  @media only screen and (min-width: 768px) {
    text-align: center;
    margin: 20px;
  }
`;

const sectionText = css`
  text-align: left;
  font-size: 1em;
  margin: 0px;
  margin-bottom: 20px;
  @media only screen and (min-width: 768px) {
    text-align: left;
    font-size: 1.25em;
    margin: 0px;
    margin-bottom: 20px;
  }
`;

const headerContainer = css`
  height: 25px;
  align-items: center;
  margin: 10px 0px;
  justify-content: space-between;
  @media only screen and (min-width: 768px) {
    height: 25px;
    align-items: center;
    margin: 20px 40px;
    justify-content: space-between;
  }
`;

const iconStyle = css`
  margin-right: 10px;
`;
const boldButton = css`
  display: inline-block;
  width: 100px;
  text-align: center;
  border-radius: 5.5px;
  font-family: "Lucida Grande", "Lucida Sans Unicode", "Lucida Sans", Geneva,
    Verdana, sans-serif;
  font-size: 15px;
  font-weight: bold;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: normal;
  text-transform: none;
`;

const titleStyle = css`
  white-space: pre-wrap;
  // white-space: nowrap;
  font-family: "Lucida Grande", "Lucida Sans Unicode", "Lucida Sans", Geneva,
    Verdana, sans-serif;
  font-size: 15px;
  line-height: 18px;
  margin-right: 10px;
  /* margin-top: 10px; */
  margin-bottom: 5px;
`;

const H1 = styled("h1")`
  font-family: "Lucida Grande", "Lucida Sans Unicode", "Lucida Sans", Geneva,
    Verdana, sans-serif;
  font-size: 25px;
  font-weight: bold;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #000000;
`;

const P = styled("p")`
  font-family: "Lucida Grande", "Lucida Sans Unicode", "Lucida Sans", Geneva,
    Verdana, sans-serif;
  font-size: 0.9375rem;
  font-weight: normal;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #000000;
  display: block;
`;

const H2 = styled("h2")`
  font-family: "Lucida Grande", "Lucida Sans Unicode", "Lucida Sans", Geneva,
    Verdana, sans-serif;
  font-size: 20px;
  /* line-height: 18px; */
  color: #1f3747;
  margin: auto;
`;

const H3 = styled("h3")`
  font-family: "Lucida Grande", "Lucida Sans Unicode", "Lucida Sans", Geneva,
    Verdana, sans-serif;
  font-size: 20px;
  line-height: 18px;
  color: #1f3747;
  margin: auto;
`;

export const buttonStyle = css`
  font-family: "Lucida Grande", "Lucida Sans Unicode", "Lucida Sans", Geneva,
    Verdana, sans-serif;
  font-size: 15px;
  font-weight: bold;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: normal;
  text-transform: none;
  box-shadow: 3px 3px 13px rgba(120, 120, 120, 0.5);
  display: inline-flex;
  align-items: center;
  border-radius: 5.5px;
`;

String.prototype.toProperCase = function () {
  return this.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
};

const ReadOnlyInvoiceDetails = (props) => {
  const [invoice, setInvoice] = useState(null);

  useEffect(() => {
    setupActiveInvoice();
  }, []);

  const setupActiveInvoice = async () => {
    // console.log("ADMIN ACCESS NOT DETECTED. READ ONLY ENABLED.\n\n");
    // console.log("Looking for uuid in url...");
    let uuid = parseQuery(props.history.location.search).id;
    // console.log("SUCCESS! Found uuid in url: " + uuid);
    // console.log("Fetching invoice using uuid...");
    const invoiceObj = await props.actions.getInvoiceByUUId(uuid);

    setInvoice(invoiceObj);
  };

  // login user
  const authenticate = async () => {
    // console.log("setting up auth...");
    const result = await props.actions.webFeatureLogin(
      appId,
      activeInvoice.orderCustomerInfo.id
    );
    const user = result.user;
    SecureLocalStorage.setItem("userId", user.id);
  };

  useEffect(() => {
    // const auth = SecureLocalStorage.getItem("auth");
    // console.log("auth: " + auth);

    if (invoice) authenticate();
  }, [invoice]);

  // console.dir(invoice);
  if (!invoice) {
    // isLoading(true);
    return null;
  }

  const { appLogo, appName, appId } = invoice;
  // console.log("its the active invoice!\n\n");
  const activeInvoice = invoice.invoice;
  // console.dir(activeInvoice);

  const DRAFT = activeInvoice.status === "DRAFT";
  const OPEN = activeInvoice.status === "OPEN";
  const PAID = activeInvoice.status === "PAID";
  const VOID = activeInvoice.status === "VOID";

  const {
    id,
    name,
    description,
    subtotal,
    status,
    createdDate,
    total,
    orderCustomerInfo: { firstName, lastName, email, phone },
  } = activeInvoice;

  const { orderCustomerInfo } = activeInvoice;

  const parsedCreatedDate = new Date(createdDate).toLocaleDateString("en-US");

  const renderCustomerDetails = () => {
    return (
      <div className={storeContainer}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            borderBottom: "1px solid #d3d3d3",
            paddingBottom: "10px",
          }}
        >
          {" "}
          <H3 className={sectionText}>Customer</H3>
          <span className={titleStyle}>
            {firstName} {lastName}
          </span>
          <span className={titleStyle}>{email} </span>
          <span className={titleStyle}>{phone}</span>
        </div>
      </div>
    );
  };

  const renderInvoiceItems = () => {
    return (
      <div className={storeContainer} style={{ marginBottom: "20px" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          {" "}
          <H3 className={sectionText} style={{ textAlign: "end" }}>
            Items
          </H3>
          {activeInvoice.invoiceItems.map((item, idx) => {
            return (
              <div key={item.name} style={{ marginBottom: "5px" }}>
                <p style={{ textAlign: "end" }}>
                  {item.quantity} x {item.name} (
                  {priceToDollars(item.pricePerItem)} each)
                </p>
              </div>
            );
          })}
          <H3
            className={sectionText}
            style={{
              marginTop: "20px",
              textAlign: "end",
              borderTop: "1px solid #d3d3d3",
              paddingTop: "10px",
            }}
          >
            Total: {priceToDollars(total)}
          </H3>
        </div>
      </div>
    );
  };

  const renderInvoiceDetails = () => {
    return (
      <div className={storeContainer}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            borderBottom: "1px solid #d3d3d3",
            paddingBottom: "10px",
          }}
        >
          {" "}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "20px",
            }}
          >
            <H3 className={sectionText} style={{ marginBottom: "0px" }}>
              {"Invoice - #" + id}
            </H3>{" "}
            {status && typeof status === "string" ? (
              <span
                style={{
                  fontSize: "0.9375rem",
                  fontWeight: "normal",
                }}
              >
                <strong>Status</strong>: {status.toProperCase()}
              </span>
            ) : (
              ""
            )}
          </div>
          <span className={titleStyle} style={{ marginBottom: "20px" }}>
            {name}
          </span>
          <H3 className={sectionText} style={{ marginBottom: "10px" }}>
            Memo:
          </H3>{" "}
          <p className={titleStyle} style={{ whiteSpace: "normal" }}>
            {description}
          </p>
        </div>
      </div>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <div>
        <div
          className={storeContainer}
          style={{
            maxWidth: "475px",
            margin: "auto",
            marginTop: "10px",
            borderRadius: "5px",
            border: "1px solid #d3d3d3",
            marginBottom: "50px",
          }}
        >
          <FlexRow
            className={headerContainer}
            style={{
              alignItems: "baseline",
              height: "auto",
              width: "inherit",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {appLogo && (
              <img
                src={appLogo}
                style={{
                  borderRadius: ".4em",
                  width: "75px",
                }}
              ></img>
            )}
            <H2 className={headerText}>{appName}</H2>
          </FlexRow>

          {renderInvoiceDetails()}
          {renderCustomerDetails()}
          {renderInvoiceItems()}
          {status === "OPEN" && (
            <Elements stripe={stripePromise}>
              <InvoicePaymentForm
                customer={orderCustomerInfo}
                total={total}
                appId={appId}
                invoiceId={id}
                history={props.history}
                status={status}
              />
            </Elements>
          )}
        </div>
      </div>
    </ThemeProvider>
  );
};

export default withRedux(ReadOnlyInvoiceDetails);
